import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Alert, Card } from 'react-bootstrap';
import { GoogleLogin } from '@react-oauth/google';
import './Login.css';
import { authService } from '../../services/api';
import axios from 'axios';
import { config } from '../../services/api';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setNeedsVerification(false);
    setResendSuccess(false);

    try {
      // Use the authService instead of direct axios call
      const response = await authService.login(formData.email, formData.password);
      
      // Notify about authentication change
      window.dispatchEvent(new Event('authChange'));
      
      // Redirect to documents page
      navigate('/');
    } catch (err) {
      console.error('Login error:', err);
      
      // Check if the error is due to an unverified email
      if (err && err.detail === 'No active account found with the given credentials') {
        setError('Votre compte n\'est pas encore activé. Veuillez vérifier votre email et cliquer sur le lien d\'activation.');
        setNeedsVerification(true);
      } else {
        setError(err.detail || 'Échec de la connexion. Veuillez vérifier vos identifiants.');
        
        } 
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setResendingEmail(true);
      setResendSuccess(false);
      
      console.log("Sending resend verification request to:", `${config.API_URL}/api/users/registration/resend-email/`);
      console.log("With email:", formData.email);
      
      // Call your backend API to resend verification email
      const response = await axios.post(`${config.API_URL}/api/users/registration/resend-email/`, {
        email: formData.email
      });
      
      console.log("Resend verification response:", response.data);
      
      setResendSuccess(true);
      setError('');
    } catch (err) {
      console.error('Error resending verification email:', err);
      console.error('Error details:', err.response?.data);
      setError('Impossible d\'envoyer l\'email de vérification. Veuillez réessayer plus tard.');
    } finally {
      setResendingEmail(false);
    }
  };

  const handleGoogleLogin = async (credentialResponse) => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Google credential response received');
      
      // Send the token to your backend
      const response = await authService.googleLogin(credentialResponse.credential);
      
      console.log('Backend login successful');
      
      // Notify about authentication change
      window.dispatchEvent(new Event('authChange'));
      
      // Redirect to home page
      navigate('/');
    } catch (err) {
      console.error('Google login error:', err);
      setError('La connexion avec Google a échoué: ' + 
        (err.response?.data?.detail || err.message || 'Erreur inconnue'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page py-5 mt-5">
      <Container>
        <Row className="justify-content-center">
          <Col md={6} lg={5}>
            <Card className="login-card">
              <Card.Body>
                <div className="text-center mb-4">
                  <h2 className="login-title">Connexion</h2>
                  <p className="login-subtitle">Accédez à votre espace personnel</p>
                </div>
                
                {error && (
                  <Alert variant="danger">
                    {error}
                    {needsVerification && (
                      <div className="mt-2">
                        <Button 
                          variant="outline-danger" 
                          size="sm" 
                          onClick={handleResendVerification}
                          disabled={resendingEmail}
                        >
                          {resendingEmail ? 'Envoi en cours...' : 'Renvoyer l\'email de vérification'}
                        </Button>
                      </div>
                    )}
                  </Alert>
                )}
                
                {resendSuccess && (
                  <Alert variant="success">
                    Un nouvel email de vérification a été envoyé. Veuillez vérifier votre boîte de réception et vos spams.
                  </Alert>
                )}
                
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Adresse Email</Form.Label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="fas fa-envelope"></i>
                      </span>
                      <Form.Control
                        type="email"
                        name="email"
                        placeholder="Entrez votre email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </Form.Group>
                  
                  <Form.Group className="mb-4">
                    <Form.Label>Mot de passe</Form.Label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="fas fa-lock"></i>
                      </span>
                      <Form.Control
                        type="password"
                        name="password"
                        placeholder="Entrez votre mot de passe"
                        value={formData.password}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </Form.Group>
                  
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <Form.Check
                      type="checkbox"
                      id="remember-me"
                      label="Se souvenir de moi"
                    />
                    <a href="/reset-password" className="forgot-password">Mot de passe oublié?</a>
                  </div>
                  
                  <Button 
                    variant="primary" 
                    type="submit" 
                    className="w-100 login-btn" 
                    disabled={loading}
                  >
                    {loading ? 'Connexion en cours...' : 'Se connecter'}
                  </Button>
                </Form>
                
                <div className="text-center my-3">
                  <p className="text-muted">- ou -</p>
                </div>
                
                <div className="d-flex justify-content-center mb-3">
                  <GoogleLogin
                    onSuccess={handleGoogleLogin}
                    onError={() => setError('La connexion avec Google a échoué')}
                    useOneTap
                    theme="filled_blue"
                    text="continue_with"
                    shape="pill"
                    locale="fr"
                  />
                </div>
                
                <div className="text-center mt-4">
                  <p className="mb-0">
                    Vous n'avez pas de compte? <a href="/register" className="register-link">S'inscrire</a>
                  </p>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Login;